import React from "react";
import { Grid, Responsive, Message, Confirm, Modal, Button, Icon, Popup, Checkbox } from "semantic-ui-react";
import SidebarTitle from "components/Sidebar/SidebarTitle";
import { Field, Form, Formik } from "formik";
import CustomInputTextComponent from "components/Form/CustomInputTextComponent";
import SecondaryButton from "components/Buttons/SecondaryButton";
import MainButton from "components/Buttons/MainButton";
import InputPhone from "components/Form/InputPhone";
import * as Yup from "yup";
import RadioButtons from "components/Form/RadioButtons";
import { formatPhoneNumber, isValidPhoneNumber } from "react-phone-number-input";
import { parsePhoneNumberFromString } from "libphonenumber-js/max";
import { searchInsurances } from "services/insurance";
import { getAllSpecialRegimes } from "services/specialRegime";
import { getAllProviders } from "services/provider";
import { searchDoctors } from "services/doctor";
import { searchPatientsMyLab, getMpiPatientByMatricule, getMatchingPatient, patchPatient, postPatient } from "services/patient";
import { searchPostalCodes } from "services/postalCode";
import { getCitiesByPostalCode, resetCities, resetWorkCities } from "services/city";
import { getStreetsByPostalCode } from "services/street";
import { connect } from "react-redux";
import Select from "components/Form/Select";
import AsyncFilterSelect from "components/Form/AsyncFilterSelect";
import debounce from "lodash.debounce";
import Separator from "components/Form/Separator";
import { formatPhoneNumberToInternational } from "utils/phone";
import { ServiceEnum } from "config/service";
import { convertMyLabToPatient, convertForPatientMatcher } from "utils/patientConverter";
import { languagesOptions, countriesOptions, orderedSpecialRegimes, doctorHandleInputChange } from "utils/selectOptions";
import moment from "moment";
import { toast } from "react-toastify";
import * as countries from "i18n-iso-countries";
import * as SpecialButtons from "components/Form/SpecialButtons";
import QRReaderPrescriptionModal from "features/FolderManager/components/modals/QRReaderPrescriptionModal";
import CustomDatePickerComponent from "components/Form/CustomDatePickerComponent";
import * as cdigit from "cdigit";
import InsuranceInput from "components/Form/InsuranceInput";
import { getAllContactComments } from "services/contactComment";
import { cleanPhoneNumber } from "utils/phone";
import { capitalize, findNumberInAddress, removeNumberFromStreet } from "utils/patientConverter";
import { flattenObject } from "utils/objectUtils";
import { displayGenderText } from "utils/displayGender";
import GranteePatientModal from "features/FoldersOverview/components/modals/GranteePatientModal";
import { isTabletOrLess } from "utils/isMobile";
import { withTranslation } from "react-i18next";

const isMobile = () => {
	return window.innerWidth - Responsive.onlyMobile.maxWidth < 0;
};

countries.registerLocale(require("i18n-iso-countries/langs/fr.json"));

const schemaValidation = (t) =>
	Yup.object().shape({
		gender: Yup.string().required(t("addPatient.requiredGender")),
		first_name: Yup.string().required(t("addPatient.requiredFirstName")),
		birth_name: Yup.string().required(t("addPatient.requiredBirthName")),
		married_name: Yup.string(),
		birth_date: Yup.date()
			.min(new Date("1900-01-01"), t("addPatient.birthMin"))
			.max(new Date(), t("addPatient.birthMax"))
			.required(t("addPatient.requiredBirth")),
		cns: Yup.string().when("ignoreCnsError", {
			is: false,
			then: Yup.string()
				.required(t("addPatient.requiredCns"))
				.test(
					"compareWithBirthDate",
					t("addPatient.birthNotEqualCns"),
					//Yup ref doesn't work with functional arrows
					function (value) {
						if (!value || value === undefined) return false;
						if (value?.length === 8 || value?.length === 13) {
							const ref = Yup.ref("birth_date");
							const birthDate = moment(this.resolve(ref)).format("YYYYMMDD");
							const birthDate2000 = moment(this.resolve(ref)).add(2000, "year").format("YYYYMMDD");
							const cnsBirthDate = value.substring(0, 8);
							return birthDate === cnsBirthDate || birthDate2000 === cnsBirthDate;
						}
						return true;
					}
				)
				.test("cnsLength", t("addPatient.cnsLength"), (value) => {
					if (!value || value === undefined) return false;
					return value.match(new RegExp("^(([0-9]{13})|([0-9]{8}))"));
				})
				.test("cnsValidation", t("addPatient.invalidCns"), (value) => {
					if (!value || value === undefined) return false;

					if (value?.length === 8) return value.match(new RegExp(process.env.REACT_APP_NO_CNS_REGEX));

					if (value?.length === 13) {
						const stringToValidate = value.substring(0, 11);
						const luhnVal = value.charAt(11);
						const verhoeffVal = value.charAt(12);
						return (
							value.match(new RegExp(process.env.REACT_APP_CNS_REGEX)) &&
							cdigit.luhn.compute(stringToValidate) === luhnVal &&
							cdigit.verhoeff.compute(stringToValidate) === verhoeffVal
						);
					}

					return false;
				}),
		}),

		patientInsurances: Yup.array().of(
			Yup.object().shape({
				insurance: Yup.object().shape({ id: Yup.number() }).nullable(),
				number: Yup.string().nullable(),
			})
		),
		email: Yup.string().email(t("addPatient.invalidEmail")),
		email_comment: Yup.string(),
		phone: Yup.string()
			.test("phoneTypeValidation", t("addPatient.notHomePhone"), (value) => {
				if (!value || value === "+352") return true;
				if (formatPhoneNumber(value) !== "") {
					const type = parsePhoneNumberFromString(value).getType();
					return type === "FIXED_LINE" || type === "FIXED_LINE_OR_MOBILE";
				} else if (value) {
					return false;
				}
				return true;
			})
			.test("phoneValidation", t("addPatient.unknownHomePhoneFormat"), (value) => {
				value = cleanPhoneNumber(value);
				return formatPhoneNumber(value) === "" || isValidPhoneNumber(value);
			}),
		phone_comment: Yup.string(),
		mobile_phone: Yup.string()
			.test("phoneTypeValidation", t("addPatient.notMobilePhone"), (value) => {
				if (value === "+352") return true;
				if (formatPhoneNumber(value) !== "") {
					const type = parsePhoneNumberFromString(value).getType();
					return type === "MOBILE" || type === "FIXED_LINE_OR_MOBILE";
				} else if (value) {
					return false;
				}
				return true;
			})
			.test("phoneValidation", t("addPatient.unknownMobilePhoneFormat"), (value) =>
				!value || value === "+352" ? true : isValidPhoneNumber(cleanPhoneNumber(value))
			),

		mobile_phone_comment: Yup.string(),
		mobile_phone_is_relative: Yup.boolean(),
		hasHomeAddress: Yup.boolean(),
		home_street: Yup.string().when("hasHomeAddress", {
			is: true,
			then: Yup.string().required(t("addPatient.requiredStreetNumber")),
		}),
		home_number: Yup.string(),
		home_complement: Yup.string(),
		home_city: Yup.string().when("hasHomeAddress", {
			is: true,
			then: Yup.string().required(t("addPatient.requiredHomeCity")),
		}),
		home_postal_code: Yup.string().when("hasHomeAddress", {
			is: true,
			then: Yup.string().required(t("addPatient.requiredZipCode")),
		}),
		home_country: Yup.string().when("hasHomeAddress", {
			is: true,
			then: Yup.string().required(t("addPatient.requiredCountry")),
		}),
		hasWorkAddress: Yup.boolean(),
		work_street: Yup.string().when("hasWorkAddress", {
			is: true,
			then: Yup.string().required(t("addPatient.requiredWorkStreet")),
		}),
		work_number: Yup.string(),
		work_complement: Yup.string(),
		work_city: Yup.string().when("hasWorkAddress", {
			is: true,
			then: Yup.string().required(t("addPatient.requiredWorkCity")),
		}),
		work_postal_code: Yup.string().when("hasWorkAddress", {
			is: true,
			then: Yup.string().required(t("addPatient.requiredWorkZip")),
		}),
		work_country: Yup.string().when("hasWorkAddress", {
			is: true,
			then: Yup.string().required(t("addPatient.requiredWorkCountry")),
		}),
		work_company: Yup.string(),
		specialRegime: Yup.object()
			.required(t("addPatient.requiredRegime"))
			.shape({
				id: Yup.number().required(t("addPatient.requiredRegime")).moreThan(0, t("addPatient.requiredRegime")),
			}),
		provider_id: Yup.number().nullable(),
		doctor: Yup.object().shape({ id: Yup.number().nullable() }).nullable(),
		languages: Yup.array().of(Yup.object()).nullable(),
	});
const styles = {
	grid: {
		marginLeft: 0,
	},
	button: {
		backgroundColor: "#00c7b1",
		height: 40,
		width: 40,
		color: "#fff",
		fontFamily: "Lato",
		textTransform: "uppercase",
		padding: 0,
		fontSize: "0.8em",
		lineHeight: "40px",
		fontWeight: 600,
		cursor: "pointer",
		outline: "none",
		border: 0,
		borderRadius: "4px",
		boxShadow: "0 0 0 1px transparent inset, 0 0 0 0 rgba(34,36,38,.15) inset",
		transition: "opacity .1s ease,background-color .1s ease,color .1s ease,box-shadow .1s ease,background .1s ease",
		":hover": {
			backgroundColor: "#00bda8",
			boxShadow: "0 0 0 1px transparent inset, 0 0 0 0 rgba(34,36,38,.15) inset",
			color: "#f5f5f5",
		},
		":active": {
			backgroundColor: "#00ae99",
			color: "#e6e6e6",
		},
		":disabled": {
			backgroundColor: "#D3D3D3",
		},
	},
	icon: {
		margin: 0,
		color: "#fff",
	},
	rowWithoutPadding: {
		padding: 0,
	},
};

class AddPatient extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			isInit: false,
			height: 0,
			hasCameraAccess: false,
			insuranceReaderModal: false,
			insuranceScanId: null,
			openIgnoreCnsError: false,
			setFieldValue: null,
			doctorLoading: false,
			postalCodeLoading: false,
			insuranceLoading: false,
			mpiPatient: null,
			mpiSelection: {},
			granteeLoading: false,
			openGranteePatientModal: false,
			granteeSelected: null,
			foundGrantee: null,
			values: null,
		};

		this.homeNumberRef = React.createRef();
		this.workNumberRef = React.createRef();
		this.formBodyRef = React.createRef();

		window.addEventListener("resize", this.updateHeight);
	}

	componentDidMount() {
		this.props.getAllContactComments();
		this.props.getAllSpecialRegimes();
		//this.props.getAllProviders();
		this.updateHeight();
		this.setState({ isInit: true });
	}

	filterPatients = debounce((searchTerm, callback) => {
		const { generalParameters } = this.props;
		this.props
			.searchPatientsMyLab({ multiple_search: searchTerm }, 20, 0)
			.then(() => {
				this.setState({ granteeLoading: false });
				callback(
					this.props.patients.search?.list?.map((patient) => {
						return {
							id: patient.id,
							label: `CNS ${patient.cns || ""} - ${displayGenderText(patient, generalParameters.data)} ${patient.last_name || patient.birth_name} ${
								patient.first_name
							} né(e) ${patient.birth_name || ""} le ${moment(patient.birth_date).format(
								process.env.REACT_APP_DATE_DISPLAY_FORMAT
							)} - ${moment().diff(patient.birth_date, "years")} ans ${patient.mobile_phone || ""} ${patient.email || ""}`,
							...patient,
						};
					})
				);
			})
			.catch((error) => {
				this.setState({ granteeLoading: false });
				callback(error);
			});
	}, 500);

	updateHeight = () => {
		this.setState({
			height: window.innerHeight,
		});
	};

	openGranteePatientModal = (grantee, setFieldValue, values) => {
		this.setState({
			openGranteePatientModal: true,
			granteeSelected: grantee ?? { grantee: null, relation: "" },
			setFieldValue: setFieldValue,
			values: values,
		});
	};
	closeGranteePatientModal = () => {
		this.setState({ openGranteePatientModal: false, granteeSelected: null });
	};
	searchGranteeByMatricule = (cns) => {
		this.props.getMpiPatientByMatricule(cns).then(async (resMpi) => {
			if (resMpi.payload && resMpi.payload.data && resMpi.payload.data.length > 0) {
				this.setState({ foundGrantee: resMpi.payload.data[0] });
			} else toast.warn(this.props.t("addPatient.notMpiDspError"));
		});
	};
	initFoundGrantee = () => {
		this.setState({ foundGrantee: null });
	};

	removeGrantee = (grant, values, setFieldValue) => {
		setFieldValue(
			"grantees",
			values.grantees.filter((g) => g.grantee.cns !== grant.grantee.cns || g.grantee.id !== grant.grantee.id)
		);
	};

	validateGrantee = (grantee, relation) => {
		let copy = this.state.values.grantees.slice();
		if (
			!!copy.find((g) => g.grantee.cns === grantee.cns && g.grantee.first_name === grantee.first_name && g.grantee.birth_name === grantee.birth_name)
		)
			copy = copy.map((g) => {
				if (g.grantee.cns === grantee.cns && g.grantee.first_name === grantee.first_name && g.grantee.birth_name === grantee.birth_name)
					return { id: g.id, grantee: grantee, relation: relation };
				return g;
			});
		else copy.push({ grantee: grantee, relation: relation });
		this.state.setFieldValue("grantees", copy);
	};

	updateOrCreateLocalPatient = async (myLabPatient) => {
		await this.props.getMatchingPatient(ServiceEnum.mylab, myLabPatient.id, convertForPatientMatcher(myLabPatient));

		const { exists } = this.props.patients;
		myLabPatient = { ...myLabPatient, externalIds: exists?.externalIds };
		const localPatient = convertMyLabToPatient(myLabPatient);
		if (!exists) {
			localPatient.id = null;
			const postPatientResponse = await this.props.postPatient(localPatient);
			localPatient.id = postPatientResponse.payload.data.id;
		} else {
			localPatient.id = exists.id;
			if (!moment(localPatient.updated_at).isBefore(moment(exists.updated_at))) {
				await this.props.patchPatient(localPatient, true);
			}
		}
		return localPatient;
	};

	filterDoctors = debounce((searchTerm, callback) => {
		this.props
			.searchDoctors({ multiple_search: searchTerm }, 30, 0)
			.then(() => {
				this.setState({ doctorLoading: false });
				callback(
					this.props.doctors.list.map((doctor) => {
						return {
							value: doctor.id,
							label: `${doctor.code} - ${doctor.last_name} ${doctor.first_name} - ${doctor.speciality} - ${doctor.address.street} ${doctor.address.postal_code} ${doctor.address.city}`,
							labels: [
								{ text: doctor.code, width: 10 },
								{ text: doctor.last_name, width: 20 },
								{ text: doctor.first_name, width: 20 },
								{ text: doctor.speciality, width: 10 },
								{ text: doctor.address?.street, width: 20 },
								{ text: doctor.address?.postal_code, width: 5 },
								{ text: doctor.address?.city, width: 15 },
							],
							...doctor,
							sil_id: doctor.external_id,
							mylab_id: doctor.id,
						};
					})
				);
			})
			.catch((error) => {
				this.setState({ doctorLoading: false });
				callback(error);
			});
	}, 500);

	filterPostalCodes = debounce((searchTerm, country, callback) => {
		this.props
			.searchPostalCodes({ code: searchTerm, country: country }, 10, 0)
			.then(() => {
				const postCodes = this.props.postalCodes.list.map((postalCode) => {
					return postalCode.cities.map((city) => {
						const defaultValue = {
							value: postalCode.code,
							label: `${postalCode.code} - ${city.name}`,
							country: postalCode.country_iso,
							city: city,
							id: postalCode.id,
						};
						if (postalCode.streets.length > 0) {
							return postalCode.streets.map((street) => {
								if (street.displayWithPostalCode)
									return {
										value: postalCode.code,
										label: `${street.name} - ${postalCode.code} - ${city.name}`,
										country: postalCode.country_iso,
										city: city,
										street: street,
										id: postalCode.id,
									};
								return defaultValue;
							});
						}
						return defaultValue;
					});
				});
				const mergedPostCodes = [].concat.apply([], [].concat.apply([], postCodes));
				this.setState({ postalCodeLoading: false });
				callback(mergedPostCodes.sort((a, b) => (a.label < b.label ? -1 : a.label > b.label ? 1 : 0)));
			})
			.catch((error) => {
				this.setState({ postalCodeLoading: false });
				callback(error);
			});
	}, 500);

	filterInsurances = debounce((searchTerm, callback) => {
		this.props
			.searchInsurances({ name: searchTerm }, 20, 0)
			.then(() => {
				this.setState({ postalCodeLoading: false });
				callback(
					this.props.insurances.list.map((insurance) => {
						return {
							id: insurance.id,
							value: insurance.id,
							label: insurance.name,
						};
					})
				);
			})
			.catch((error) => {
				this.setState({ postalCodeLoading: false });
				callback(error);
			});
	}, 500);

	autofillDataPatient = async (cns, setFieldValue) => {
		await this.props.getMpiPatientByMatricule(cns).then(async (res) => {
			if (res && res.payload.data && res.payload.data.error) toast.warn(this.props.t("addPatient.notMpiDspError"));
			else {
				const results = res.payload.data;
				const { mpiSelection } = this.state;
				if (results && results.filter((p) => p.matricule === cns.toString()).length >= 1) {
					const patient = results.filter((p) => p.matricule === cns.toString())[0];
					this.setState({
						mpiPatient: { ...patient, cns: patient.matricule },
						setFieldValue: setFieldValue,
						mpiSelection: {
							gender: mpiSelection.gender ?? !!patient.gender,
							birth_name: mpiSelection.birth_name ?? !!patient.birth_name,
							marital_name: mpiSelection.marital_name ?? !!patient.marital_name,
							first_name: mpiSelection.first_name ?? !!patient.first_name,
							birth_date: mpiSelection.birth_date ?? !!patient.birth_date,
							phone: mpiSelection.phone ?? !!patient.phone,
							mobile_phone: mpiSelection.mobile_phone ?? !!patient.mobile_phone,
							email: mpiSelection.email ?? !!patient.email,
							address: mpiSelection.address ?? !!patient.address,
						},
					});
				} else {
					toast.warn(this.props.t("addPatient.notMpiDspError"));
				}
			}
		});
	};

	onChangeMpiSelection = (prop) => {
		let { mpiSelection } = this.state;
		mpiSelection[prop] = !mpiSelection[prop];
		this.setState({ mpiSelection: mpiSelection });
	};

	cancelAutofillFromMpi = () => {
		this.setState({ mpiPatient: null });
	};

	confirmAutofillFromMpi = () => {
		const { mpiPatient, setFieldValue, mpiSelection } = this.state;
		if (mpiSelection.birth_name && mpiPatient.birth_name) setFieldValue("birth_name", mpiPatient.birth_name.toUpperCase());
		if (mpiSelection.marital_name && mpiPatient.marital_name) setFieldValue("married_name", mpiPatient.marital_name.toUpperCase());
		if (mpiSelection.first_name && mpiPatient.first_name) setFieldValue("first_name", capitalize(mpiPatient.first_name));
		if (mpiSelection.birth_date && mpiPatient.birth_date) setFieldValue("birth_date", moment(mpiPatient.birth_date).format("YYYY-MM-DD"));
		if (mpiSelection.gender && mpiPatient.gender) setFieldValue("gender", mpiPatient.gender.toLowerCase());
		if (mpiSelection.phone && mpiPatient.phone) setFieldValue("phone", mpiPatient.phone);
		if (mpiSelection.mobile_phone && mpiPatient.mobile_phone && mpiPatient.mobile_phone.length > 0) {
			setFieldValue("mobile_phone", mpiPatient.mobile_phone);
		}
		if (mpiSelection.email && mpiPatient.email && mpiPatient.email.length > 0) {
			setFieldValue("email", mpiPatient.email);
		}
		if (mpiSelection.address && mpiPatient.address) {
			setFieldValue("home_number", findNumberInAddress(mpiPatient.address.street));
			setFieldValue("home_street", removeNumberFromStreet(findNumberInAddress(mpiPatient.address.street), mpiPatient.address.street));
			setFieldValue("home_postal_code", mpiPatient.address.postal_code);
			setFieldValue("home_city", mpiPatient.address.city);
			setFieldValue("home_country", countries.alpha3ToAlpha2(mpiPatient.address.country));
		}

		this.setState({ mpiPatient: null });
	};

	initCnsWithFilter = () => {
		const { filter } = this.props;
		if (filter) {
			if (filter.match(new RegExp(process.env.REACT_APP_CNS_REGEX)) !== null) {
				return parseInt(filter);
			}
		}
		return "";
	};
	initBirthDateWithFilter = () => {
		const { filter } = this.props;
		if (filter && filter.match(new RegExp(process.env.REACT_APP_CNS_REGEX)) !== null) {
			return moment(filter.substring(0, 8), "YYYYMMDD", "YYYY-MM-DD");
		}
		return "";
	};

	openInsuranceReaderModal = (index) => {
		const scanAvailableState = {
			hasCameraAccess: true,
			insuranceReaderModal: true,
			insuranceScanId: index,
		};
		this.setState(scanAvailableState);
	};

	closeInsuranceReaderModal = () => {
		this.setState({
			insuranceReaderModal: false,
		});
	};

	submitInsuranceReaderModal = (value, setFieldValue) => {
		setFieldValue(`patientInsurances.${this.state.insuranceScanId}.number`, value);
		this.closeInsuranceReaderModal();
	};

	openIgnoreCnsErrorConfirm = () => this.setState({ openIgnoreCnsError: true });

	confirmIgnoreCnsErrorConfirm = (setFieldValue) => {
		setFieldValue("ignoreCnsError", true);
		this.setState({ openIgnoreCnsError: false });
	};

	cancelIgnoreCnsErrorConfirm = () => this.setState({ openIgnoreCnsError: false });

	isSpecialRegimeCee = (specialRegimeId) => {
		const { specialRegimes } = this.props;
		const specialRegime = specialRegimes.list.find((sr) => sr.id === specialRegimeId);

		return specialRegime?.geslab_id === process.env.REACT_APP_DEFAULT_GESLAB_CEE_ID;
	};

	activateComment = (type, setFieldValue) => {
		setFieldValue(type, true);
	};
	disactivateComment = (type, setFieldValue) => {
		setFieldValue(type, false);
	};

	render() {
		const { hideSidebar, onSubmit, specialRegimes, cities, streets, patients, contactComments, filter, t } = this.props;
		const { isInit, mpiPatient, mpiSelection } = this.state;

		return (
			<div style={{ width: "100%", height: "100%", display: "flex" }}>
				{}
				<Responsive
					minWidth={1024}
					as="div"
					style={{
						width: "0",
						padding: "1rem",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				></Responsive>
				<div
					style={{
						width: "100%",
						backgroundColor: "rgb(236, 240, 244)",
						padding: "2rem",
					}}
				>
					<Grid>
						<SidebarTitle title={t("addPatient.newPatient")} />
						<Grid.Row columns={1}>
							<Grid.Column style={{ padding: 0 }}>
								<Formik
									initialValues={{
										gender: "",
										married_name: "",
										first_name: "",
										birth_date: filter ? this.initBirthDateWithFilter() : "",
										birth_name: "",
										cns: filter ? this.initCnsWithFilter() : "",
										ignoreCnsError: false,
										patientInsurances: [
											{
												insurance: null,
												number: "",
											},
											{
												insurance: null,
												number: "",
											},
										],
										hasSecondInsurance: false,
										hasFirstInsurance: false,
										hasDoctor: false,
										phone_comment: "",
										phone: "",
										mobile_phone_comment: "",
										mobile_phone: "",
										mobile_phone_is_relative: false,
										email: "",
										email_comment: "",
										hasEmailComment: false,

										reference: "",
										hasHomeAddress: true,
										home_street: "",
										home_complement: "",
										home_country: "",
										home_city: "",
										home_postal_code: "",

										hasWorkAddress: false,
										work_number: "",
										work_street: "",
										work_complement: "",
										work_country: "",
										work_city: "",
										work_postal_code: "",
										work_company: "",

										specialRegime: {
											id: 0,
										},
										provider_id: null,
										doctor: { id: null },
										languages: null,
										grantees: [],
									}}
									validationSchema={schemaValidation(t)}
									onSubmit={(values) => {
										if (!this.state.addAppointment) delete values.appointmentDate;
										onSubmit(values);
									}}
								>
									{({ setFieldValue, errors, touched, values, handleSubmit, handleChange }) => {
										const errorItems = Object.keys(errors)
											.map((key) => {
												if (touched[key]) return errors[key];
												return null;
											})
											.filter((notNull) => notNull);

										if (!isInit && values.cns !== "") this.autofillDataPatient(values.cns, setFieldValue);

										const isSpecialRegimeCee = this.isSpecialRegimeCee(values.specialRegime.id);

										if (isSpecialRegimeCee && !values.ignoreCnsError) {
											setFieldValue("ignoreCnsError", true);
										}

										return (
											<Form>
												<div
													style={{
														overflowY: "auto",
														height: this.state.height - 125,
														marginTop: -15,
														marginBottom: 15,
														width: "100%",
													}}
													ref={this.formBodyRef}
												>
													<Grid className="gridWithNoMarginMobile" style={{ width: "100%" }}>
														{errorItems.length > 0 && (
															<Grid.Row style={{ marginRight: 5, marginTop: 5 }}>
																<Grid.Column mobile={16} tablet={16} computer={16}>
																	<Message negative>
																		<Message.Header>{t("addPatient.errors")}</Message.Header>
																		<Message.List items={flattenObject(errorItems)} />
																	</Message>
																</Grid.Column>
															</Grid.Row>
														)}
														<Grid style={{ width: "100%" }}>
															<Grid.Row columns={2} divided>
																{/* 1e colonne	 */}
																<Grid.Column mobile={16} tablet={16} computer={8}>
																	<Grid className="gridWithNoMarginMobile">
																		<Grid.Row style={{ paddingBottom: 0, paddingRight: "15px" }}>
																			<Grid.Column mobile={16} tablet={16} computer={10}>
																				<Field
																					component={Select}
																					name="specialRegime.id"
																					customlabel={t("addPatient.regime")}
																					placeholder={t("addPatient.regimePlaceHolder")}
																					options={orderedSpecialRegimes(specialRegimes.list)}
																					required
																				/>
																			</Grid.Column>
																			<Grid.Column mobile={14} tablet={14} computer={5} style={{ paddingRight: "0" }}>
																				<Field
																					type="text"
																					name="cns"
																					customlabel={t("addPatient.cnsNumber")}
																					placeholder=""
																					component={CustomInputTextComponent}
																					onKeyUp={(e) => {
																						const val = e.target.value;
																						setTimeout(() => {
																							if (val?.length === 8 || val?.length === 13) {
																								let birth_date = moment(val.substring(0, 8), "YYYYMMDD", "YYYY-MM-DD");
																								//If the year of birth in the CNS is older than today, the CNS number was created with year of birth +2000
																								if (birth_date.year() > moment().year()) birth_date = birth_date.add(-2000, "year");
																								if (birth_date.isValid()) setFieldValue("birth_date", birth_date.format("YYYY-MM-DD"));
																							}
																						}, 500);
																					}}
																					required
																				/>
																			</Grid.Column>
																			<Grid.Column
																				mobile={2}
																				tablet={2}
																				computer={1}
																				style={{
																					display: "flex",
																					justifyContent: "flex-start",
																					alignItems: "baseline",
																					paddingLeft: 0,
																					paddingTop: "26px",
																				}}
																			>
																				<Popup
																					content={t("addPatient.searchPatientData")}
																					position="top right"
																					trigger={
																						<Button
																							color="green"
																							type="button"
																							icon={patients.loading ? <Icon loading name="spinner" /> : "search"}
																							disabled={!values.cns || values.cns.toString().length !== 13 || errors.cns}
																							onClick={(e) => {
																								if (e !== undefined) e.preventDefault();
																								this.autofillDataPatient(values.cns, setFieldValue);
																							}}
																						/>
																					}
																				/>
																			</Grid.Column>
																			{((errors.cns && touched.cns) || values.ignoreCnsError) && (
																				<>
																					<Grid.Column mobile={16} tablet={16} computer={10}></Grid.Column>
																					<Grid.Column mobile={16} tablet={16} computer={6} style={{ marginTop: -10 }}>
																						<Field
																							customlabel={t("addPatient.ignoreCnsNumber")}
																							labelStyle={{ color: "red" }}
																							component={SpecialButtons.Checkbox}
																							name="ignoreCnsError"
																							id="ignoreCnsError"
																							onChange={(e) => {
																								if (!values.ignoreCnsError) this.openIgnoreCnsErrorConfirm();
																								else handleChange(e);
																							}}
																						/>
																					</Grid.Column>
																				</>
																			)}
																		</Grid.Row>

																		{((errors.cns && touched.cns) || values.ignoreCnsError) && <Grid.Row style={styles.rowWithoutPadding}></Grid.Row>}
																		<Grid.Row style={styles.rowWithoutPadding}>
																			<Grid.Column mobile={16} tablet={16} computer={5} style={{ paddingRight: 0 }}>
																				<RadioButtons
																					name="gender"
																					field1={{ id: "m", customlabel: t("addPatient.man") }}
																					field2={{ id: "f", customlabel: t("addPatient.women") }}
																					customlabel={t("addPatient.gender")}
																					errors={errors}
																					touched={touched}
																					required
																				/>
																			</Grid.Column>
																			<Grid.Column mobile={16} tablet={16} computer={6}>
																				<Field
																					name="birth_date"
																					customlabel={t("addPatient.birthday")}
																					max={moment().toDate()}
																					component={CustomDatePickerComponent}
																					required
																				/>
																			</Grid.Column>
																		</Grid.Row>
																		<Grid.Row style={styles.rowWithoutPadding}>
																			<Grid.Column mobile={16} tablet={16} computer={5}>
																				<Field
																					type="text"
																					name="birth_name"
																					customlabel={t("addPatient.nameAtBirth")}
																					placeholder=""
																					component={CustomInputTextComponent}
																					required
																					forceUpperCase
																				/>
																			</Grid.Column>
																			<Grid.Column mobile={16} tablet={16} computer={6}>
																				<Field
																					type="text"
																					name="married_name"
																					customlabel={t("addPatient.marriedName")}
																					placeholder=""
																					component={CustomInputTextComponent}
																					forceUpperCase
																				/>
																			</Grid.Column>
																			<Grid.Column mobile={16} tablet={16} computer={5}>
																				<Field
																					type="text"
																					name="first_name"
																					customlabel={t("addPatient.firstName")}
																					placeholder=""
																					component={CustomInputTextComponent}
																					required
																					forceCapitalize
																				/>
																			</Grid.Column>
																		</Grid.Row>

																		<Grid.Row style={styles.rowWithoutPadding}>
																			<Grid.Column mobile={16} tablet={16} computer={5}>
																				<Field
																					component={InputPhone}
																					name="mobile_phone"
																					id="mobile_phone"
																					customlabel={
																						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
																							{t("addPatient.mobilePhone")}
																							{!values.hasMobileComment && (
																								<Popup
																									content={t("addPatient.addCommentary")}
																									position="top right"
																									trigger={
																										<Button
																											style={{ padding: "3px" }}
																											size="mini"
																											icon="edit outline"
																											onClick={(e) => {
																												if (e !== undefined) e.preventDefault();
																												this.activateComment("hasMobileComment", setFieldValue);
																											}}
																										/>
																									}
																								/>
																							)}
																						</div>
																					}
																				/>

																				{values.hasMobileComment && (
																					<Field
																						component={Select}
																						isCreatable
																						name="mobile_phone_comment"
																						customlabel={
																							<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
																								{t("addPatient.mobilePhoneComment")}
																								{values.hasMobileComment && (
																									<Popup
																										content={t("addPatient.deleteComment")}
																										position="top right"
																										trigger={
																											<Button
																												style={{ padding: "3px" }}
																												size="mini"
																												icon="close"
																												onClick={(e) => {
																													if (e !== undefined) e.preventDefault();
																													this.disactivateComment("hasMobileComment", setFieldValue);
																												}}
																											/>
																										}
																									/>
																								)}
																							</div>
																						}
																						options={contactComments.list.map((reason) => {
																							return {
																								value: reason.label,
																								label: reason.label,
																							};
																						})}
																					/>
																				)}
																			</Grid.Column>
																			<Grid.Column mobile={16} tablet={16} computer={6}>
																				<Field
																					type="text"
																					name="email"
																					placeholder=""
																					customlabel={
																						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
																							{t("addPatient.mail")}
																							{!values.hasEmailComment && (
																								<Popup
																									content={t("addPatient.addCommentary")}
																									position="top right"
																									trigger={
																										<Button
																											style={{ padding: "3px" }}
																											size="mini"
																											icon="edit outline"
																											onClick={(e) => {
																												if (e !== undefined) e.preventDefault();
																												this.activateComment("hasEmailComment", setFieldValue);
																											}}
																										/>
																									}
																								/>
																							)}
																						</div>
																					}
																					component={CustomInputTextComponent}
																				/>

																				{values.hasEmailComment && (
																					<Field
																						component={Select}
																						isCreatable
																						name="email_comment"
																						customlabel={
																							<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
																								{t("addPatient.mailCommentary")}
																								{values.hasEmailComment && (
																									<Popup
																										content={t("addPatient.deleteComment")}
																										position="top right"
																										trigger={
																											<Button
																												style={{ padding: "3px" }}
																												size="mini"
																												icon="close"
																												onClick={(e) => {
																													if (e !== undefined) e.preventDefault();
																													this.disactivateComment("hasEmailComment", setFieldValue);
																												}}
																											/>
																										}
																									/>
																								)}
																							</div>
																						}
																						options={contactComments.list.map((reason) => {
																							return {
																								value: reason.label,
																								label: reason.label,
																							};
																						})}
																					/>
																				)}
																			</Grid.Column>
																			<Grid.Column mobile={16} tablet={16} computer={5}>
																				<Field
																					component={InputPhone}
																					name="phone"
																					id="phone"
																					customlabel={
																						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
																							{t("addPatient.homePhone")}
																							{!values.hasFixeComment && (
																								<Popup
																									content={t("addPatient.addCommentary")}
																									position="top right"
																									trigger={
																										<Button
																											style={{ padding: "3px" }}
																											size="mini"
																											icon="edit outline"
																											onClick={(e) => {
																												if (e !== undefined) e.preventDefault();
																												this.activateComment("hasFixeComment", setFieldValue);
																											}}
																										/>
																									}
																								/>
																							)}
																						</div>
																					}
																				/>
																				{values.hasFixeComment && (
																					<Field
																						component={Select}
																						isCreatable
																						name="phone_comment"
																						customlabel={
																							<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
																								{t("addPatient.homePhoneComment")}
																								{values.hasFixeComment && (
																									<Popup
																										content={t("addPatient.deleteComment")}
																										position="top right"
																										trigger={
																											<Button
																												style={{ padding: "3px" }}
																												size="mini"
																												icon="close"
																												onClick={(e) => {
																													if (e !== undefined) e.preventDefault();
																													this.disactivateComment("hasFixeComment", setFieldValue);
																												}}
																											/>
																										}
																									/>
																								)}
																							</div>
																						}
																						options={contactComments.list.map((reason) => {
																							return {
																								value: reason.label,
																								label: reason.label,
																							};
																						})}
																					/>
																				)}
																			</Grid.Column>
																		</Grid.Row>
																		<Grid.Row style={styles.rowWithoutPadding}>
																			<Grid.Column mobile={16} tablet={16} computer={16}>
																				<Field
																					customlabel={t("addPatient.home")}
																					component={SpecialButtons.Checkbox}
																					name="hasHomeAddress"
																					id="hasHomeAddress"
																					onChange={(e) => {
																						handleChange(e);
																						if (values.hasHomeAddress) {
																							setFieldValue("home_country", "");
																							setFieldValue("home_postal_code", "");
																							setFieldValue("home_city", "");
																							setFieldValue("home_street", "");
																							setFieldValue("home_number", "");
																						}
																					}}
																				/>
																			</Grid.Column>
																			{values.hasHomeAddress && (
																				<>
																					<Grid.Column mobile={16} tablet={16} computer={16}>
																						<Field
																							component={Select}
																							name="home_country"
																							customlabel={t("addPatient.country")}
																							placeholder={t("addPatient.selectCountry")}
																							options={countriesOptions()}
																							required
																						/>
																					</Grid.Column>

																					<Grid.Column mobile={16} tablet={8} computer={8}>
																						<Field
																							defaultValue={{
																								value: values.home_postal_code,
																								label: values.home_postal_code,
																							}}
																							component={AsyncFilterSelect}
																							name="home_postal_code"
																							customlabel={t("addPatient.zipCode")}
																							promiseOptions={(code, callback) => {
																								this.setState({
																									postalCodeLoading: true,
																								});
																								return this.filterPostalCodes(code, values.home_country, callback);
																							}}
																							isLoading={this.state.postalCodeLoading}
																							customOnChange={async (option) => {
																								if (values.home_country === "") {
																									setFieldValue("home_country", option.country);
																								}
																								setFieldValue("home_postal_code", option.value);
																								this.props.getCitiesByPostalCode(option.id).then(() => {
																									setFieldValue("home_city", option.city.name);

																									if (option.city.name) this.homeNumberRef.current.focus();
																								});

																								this.props.getStreetsByPostalCode(option.id).then(() => {
																									setFieldValue("home_street", option.street?.name || "");
																								});
																							}}
																							isCreatable
																							onCreateClick={(val) => {
																								setFieldValue("home_postal_code", val);
																								resetCities();
																							}}
																							required
																						/>
																					</Grid.Column>

																					<Grid.Column mobile={16} tablet={8} computer={8}>
																						<Field
																							component={Select}
																							name="home_city"
																							customlabel={t("addPatient.city")}
																							options={
																								values.home_postal_code
																									? cities.list.map((city) => {
																											return {
																												value: city.name,
																												label: city.name,
																											};
																									  })
																									: []
																							}
																							isCreatable
																							required
																						/>
																					</Grid.Column>

																					<Grid.Column mobile={16} tablet={4} computer={4}>
																						<Field
																							type="text"
																							name="home_number"
																							customlabel={t("addPatient.homeNumber")}
																							placeholder=""
																							component={CustomInputTextComponent}
																							innerref={this.homeNumberRef}
																						/>
																					</Grid.Column>

																					<Grid.Column mobile={16} tablet={12} computer={12}>
																						<Field
																							component={Select}
																							name="home_street"
																							customlabel={t("addPatient.street")}
																							options={
																								values.home_postal_code
																									? streets.list.map((street) => {
																											return {
																												value: street.name,
																												label: street.name,
																											};
																									  })
																									: []
																							}
																							isCreatable
																							required
																						/>
																					</Grid.Column>
																					<Grid.Column mobile={16} tablet={16} computer={16}>
																						<Field
																							type="text"
																							name="home_complement"
																							customlabel={t("addPatient.complement")}
																							placeholder={t("addPatient.complementPlaceHolder")}
																							component={CustomInputTextComponent}
																						/>
																					</Grid.Column>
																				</>
																			)}
																		</Grid.Row>
																	</Grid>
																</Grid.Column>
																{/* 2e colonne	 */}
																<Grid.Column mobile={16} tablet={16} computer={8} style={{ paddingTop: isTabletOrLess() ? "10px" : 0 }}>
																	<Grid className="gridWithNoMarginMobile">
																		<Grid.Row style={{ paddingBottom: 0 }}>
																			<Grid.Column mobile={16} tablet={16} computer={16}>
																				<Field
																					customlabel={t("addPatient.doctor")}
																					component={SpecialButtons.Checkbox}
																					name="hasDoctor"
																					id="hasDoctor"
																					onChange={(e) => {
																						handleChange(e);
																						if (values.hasDoctor) {
																							setFieldValue("doctor", null);
																						}
																					}}
																				/>
																				<Field
																					component={AsyncFilterSelect}
																					name="doctor"
																					customlabel={t("addPatient.doctor")}
																					promiseOptions={(searchTerm, callback) => {
																						this.setState({ doctorLoading: true });
																						return this.filterDoctors(searchTerm, callback);
																					}}
																					onInputChange={doctorHandleInputChange}
																					isLoading={this.state.doctorLoading}
																				/>
																			</Grid.Column>
																		</Grid.Row>
																		<Grid.Row style={styles.rowWithoutPadding}>
																			<Grid.Column mobile={16} tablet={16} computer={16} style={{}}>
																				<Field
																					customlabel={t("addPatient.firstInsurance")}
																					component={SpecialButtons.Checkbox}
																					name="hasFirstInsurance"
																					id="hasFirstInsurance"
																					onChange={(e) => {
																						handleChange(e);
																						if (!values.hasFirstInsurance) {
																							setFieldValue("patientInsurances.0.insurance", null);
																							setFieldValue("patientInsurances.0.number", null);
																						}
																					}}
																				/>
																				{values.hasFirstInsurance && (
																					<InsuranceInput
																						promiseOptions={(searchTerm, callback) => {
																							this.setState({ insuranceLoading: true });
																							this.filterInsurances(searchTerm, callback);
																						}}
																						insuranceLoading={this.state.insuranceLoading}
																						mainFieldName="patientInsurances.0.insurance"
																						mainFieldDefaultValue={values.patientInsurances[0].insurance}
																						numberFieldName="patientInsurances.0.number"
																						qrCodeButtonOnClick={(e) => {
																							if (e !== undefined) e.preventDefault();
																							this.openInsuranceReaderModal(0, setFieldValue);
																						}}
																						rowStyle={{ marginRight: 5 }}
																					/>
																				)}
																			</Grid.Column>
																		</Grid.Row>

																		<Grid.Row style={styles.rowWithoutPadding}>
																			{values.hasFirstInsurance && (
																				<Grid.Column mobile={16} tablet={16} computer={16}>
																					<Field
																						customlabel={t("addPatient.secondInsurance")}
																						component={SpecialButtons.Checkbox}
																						name="hasSecondInsurance"
																						id="hasSecondInsurance"
																						onChange={(e) => {
																							handleChange(e);
																							if (values.hasSecondInsurance) {
																								setFieldValue("patientInsurances.1.insurance", null);
																								setFieldValue("patientInsurances.1.number", "");
																							}
																						}}
																					/>
																					{values.hasSecondInsurance && (
																						<InsuranceInput
																							promiseOptions={(searchTerm, callback) => {
																								this.setState({ insuranceLoading: true });
																								this.filterInsurances(searchTerm, callback);
																							}}
																							insuranceLoading={this.state.insuranceLoading}
																							mainFieldName="patientInsurances.1.insurance"
																							mainFieldDefaultValue={values.patientInsurances[1].insurance}
																							numberFieldName="patientInsurances.1.number"
																							qrCodeButtonOnClick={(e) => {
																								if (e !== undefined) e.preventDefault();
																								this.openInsuranceReaderModal(1, setFieldValue);
																							}}
																							rowStyle={{
																								marginBottom: 20,
																							}}
																						/>
																					)}
																				</Grid.Column>
																			)}
																			<Grid.Column mobile={16} tablet={16} computer={16}>
																				<Field
																					component={Select}
																					name="languages"
																					customlabel={t("addPatient.languages")}
																					isMulti
																					options={languagesOptions()}
																					placeholder={t("addPatient.languagesPlaceHolder")}
																				/>
																			</Grid.Column>
																		</Grid.Row>
																		<Grid.Row>
																			<Grid style={{ width: "100%", margin: "5px 15px", padding: "10px", backgroundColor: "rgba(0, 167, 148,0.08)" }}>
																				<Grid.Row style={{ display: "flex", marginRight: 5, justifyContent: "flex-start", alignItems: "flex-end" }}>
																					<Grid.Column mobile={14} tablet={14} computer={14}>
																						<Field
																							component={AsyncFilterSelect}
																							name="grantee"
																							customlabel={t("addPatient.grantee")}
																							placeholder={t("addPatient.granteePlaceHolder")}
																							promiseOptions={(searchTerm, callback) => {
																								this.setState({ granteeLoading: true });
																								return this.filterPatients(searchTerm, callback);
																							}}
																							isLoading={this.state.granteeLoading}
																							customOnChange={async (option) =>
																								this.openGranteePatientModal(
																									{ grantee: await this.updateOrCreateLocalPatient(option), relation: "" },
																									setFieldValue,
																									values
																								)
																							}
																						/>
																					</Grid.Column>
																					<Grid.Column mobile={2} tablet={2} computer={2}>
																						<Button
																							style={styles.button}
																							onClick={(e) => {
																								if (e !== undefined) e.preventDefault();
																								this.openGranteePatientModal({ grantee: null, relation: "" }, setFieldValue, values);
																							}}
																						>
																							<Icon name="add user" style={styles.icon} />
																						</Button>
																					</Grid.Column>
																				</Grid.Row>

																				<Grid.Row style={{ marginRight: 5, paddingTop: 0 }}>
																					<Grid.Column>
																						<Grid
																							style={{
																								margin: "1px 1px",
																								backgroundColor: "white",
																								fontSize: "0.9em",
																								minHeigth: "25px",
																								borderColor: "hsl(0,0%,80%)",
																								borderRadius: "4px",
																								borderStyle: "solid",
																								borderWidth: "1px",
																								padding: "1px",
																							}}
																						>
																							{values.grantees &&
																								values.grantees.length > 0 &&
																								values.grantees.map((g) => (
																									<Grid.Row columns={!isMobile() ? 3 : 2} key={g.grantee?.cns + g.relation}>
																										<Grid.Column mobile={13} tablet={6} computer={7}>
																											{g.grantee?.last_name?.toUpperCase() || g.grantee?.birth_name?.toUpperCase()}{" "}
																											{capitalize(g.grantee?.first_name)} (<b>{g.relation ?? ""}</b>)
																											{isMobile() ? (
																												<div>
																													{g.grantee?.mobile_phone ?? ""} {g.grantee?.mobile_phone && g.grantee?.email ? "   " : ""}{" "}
																													{g.grantee?.email ?? ""}
																												</div>
																											) : (
																												""
																											)}
																										</Grid.Column>
																										{!isMobile() ? (
																											<Grid.Column mobile={6} tablet={6} computer={7}>
																												{g.grantee?.mobile_phone ?? ""} {g.grantee?.mobile_phone && g.grantee?.email ? "   " : ""}{" "}
																												{g.grantee?.email ?? ""}
																											</Grid.Column>
																										) : (
																											""
																										)}
																										<Grid.Column mobile={3} tablet={2} computer={2} style={{ padding: "0" }}>
																											<Button
																												style={{ padding: "5px" }}
																												size="mini"
																												icon="pencil alternate"
																												onClick={(e) => {
																													if (e !== undefined) e.preventDefault();
																													this.openGranteePatientModal(g, setFieldValue, values);
																												}}
																											/>
																											<Button
																												style={{ padding: "5px" }}
																												size="mini"
																												icon="close"
																												onClick={(e) => {
																													if (e !== undefined) e.preventDefault();
																													this.removeGrantee(g, values, setFieldValue);
																												}}
																											/>
																										</Grid.Column>
																									</Grid.Row>
																								))}
																							{values.grantees && values.grantees.length === 0 && (
																								<Grid.Row columns={1}>
																									<Grid.Column mobile={16} tablet={4} computer={16}>
																										{t("addPatient.noGrantee")}
																									</Grid.Column>
																								</Grid.Row>
																							)}
																						</Grid>
																					</Grid.Column>
																				</Grid.Row>
																			</Grid>
																		</Grid.Row>
																		<Grid.Row style={{ marginRight: 5 }}>
																			<Grid.Column mobile={16} tablet={16} computer={16}>
																				<Field
																					customlabel={t("addPatient.otherAdress")}
																					component={SpecialButtons.Checkbox}
																					name="hasWorkAddress"
																					id="hasWorkAddress"
																					onChange={(e) => {
																						handleChange(e);
																						if (values.hasWorkAddress) {
																							setFieldValue("work_company", "");
																							setFieldValue("work_country", "");
																							setFieldValue("work_postal_code", "");
																							setFieldValue("work_city", "");
																							setFieldValue("work_street", "");
																							setFieldValue("work_number", "");
																						}
																					}}
																				/>
																			</Grid.Column>
																			{values.hasWorkAddress && (
																				<>
																					<Grid.Column mobile={16} tablet={16} computer={16}>
																						<Field
																							type="text"
																							name="work_company"
																							customlabel={t("addPatient.company")}
																							placeholder=""
																							component={CustomInputTextComponent}
																						/>
																					</Grid.Column>

																					<Grid.Column mobile={16} tablet={16} computer={16}>
																						<Field
																							component={Select}
																							name="work_country"
																							customlabel={t("addPatient.country")}
																							placeholder={t("addPatient.selectCountry")}
																							options={countriesOptions()}
																							required
																						/>
																					</Grid.Column>
																					<Grid.Column mobile={16} tablet={8} computer={8}>
																						<Field
																							defaultValue={{
																								value: values.work_postal_code,
																								label: values.work_postal_code,
																							}}
																							component={AsyncFilterSelect}
																							name="work_postal_code"
																							customlabel={t("addPatient.zipCode")}
																							promiseOptions={(code, callback) => {
																								this.setState({
																									postalCodeLoading: true,
																								});
																								return this.filterPostalCodes(code, values.work_country, callback);
																							}}
																							isLoading={this.state.postalCodeLoading}
																							customOnChange={async (option) => {
																								if (values.work_country === "") {
																									setFieldValue("work_country", option.country);
																								}
																								setFieldValue("work_postal_code", option.value);
																								this.props.getCitiesByPostalCode(option.id, true).then(() => {
																									setFieldValue("work_city", option.city.name);

																									if (option.city.name) this.workNumberRef.current.focus();
																								});
																								this.props.getStreetsByPostalCode(option.id, true).then(() => {
																									setFieldValue("work_street", option.street?.name || "");
																								});
																							}}
																							isCreatable
																							onCreateClick={(val) => {
																								setFieldValue("work_postal_code", val);
																								resetWorkCities();
																							}}
																							required
																						/>
																					</Grid.Column>

																					<Grid.Column mobile={16} tablet={8} computer={8}>
																						<Field
																							component={Select}
																							name="work_city"
																							customlabel={t("addPatient.city")}
																							options={
																								values.work_postal_code
																									? cities.workList.map((city) => {
																											return {
																												value: city.name,
																												label: city.name,
																											};
																									  })
																									: []
																							}
																							isCreatable
																							required
																						/>
																					</Grid.Column>

																					<Grid.Column mobile={16} tablet={4} computer={4}>
																						<Field
																							innerref={this.workNumberRef}
																							type="text"
																							name="work_number"
																							customlabel={t("addPatient.homeNumber")}
																							placeholder=""
																							component={CustomInputTextComponent}
																						/>
																					</Grid.Column>
																					<Grid.Column mobile={16} tablet={12} computer={12}>
																						<Field
																							component={Select}
																							name="work_street"
																							customlabel={t("addPatient.street")}
																							options={
																								values.home_postal_code
																									? streets.workList.map((street) => {
																											return {
																												value: street.name,
																												label: street.name,
																											};
																									  })
																									: []
																							}
																							isCreatable
																							required
																						/>
																					</Grid.Column>
																					<Grid.Column mobile={16} tablet={16} computer={16}>
																						<Field
																							type="text"
																							name="work_complement"
																							customlabel={t("addPatient.homeNumber")}
																							placeholder={t("addPatient.complementPlaceHolder")}
																							component={CustomInputTextComponent}
																						/>
																					</Grid.Column>
																				</>
																			)}
																		</Grid.Row>
																	</Grid>
																</Grid.Column>
															</Grid.Row>
														</Grid>
													</Grid>
												</div>
												<Grid>
													<Separator />
													<Grid.Row
														columns={3}
														style={{
															paddingTop: 10,
															marginRight: 5,
															marginLeft: 5,
														}}
													>
														<Grid.Column>
															<SecondaryButton
																type="button"
																style={{ width: "100%" }}
																text={t("addPatient.cancel")}
																onClick={(e) => {
																	if (e !== undefined) e.preventDefault();
																	hideSidebar();
																}}
															/>
														</Grid.Column>
														<Grid.Column></Grid.Column>
														<Grid.Column>
															<MainButton
																style={{ width: "100%" }}
																text={t("addPatient.submit")}
																type="submit"
																onClick={() => {
																	handleSubmit();
																	this.formBodyRef.current.scrollTop = 0;
																}}
															/>
														</Grid.Column>
													</Grid.Row>
												</Grid>

												<QRReaderPrescriptionModal
													open={this.state.insuranceReaderModal}
													close={this.closeInsuranceReaderModal}
													submit={(value) => {
														this.submitInsuranceReaderModal(value, setFieldValue);
													}}
													hasCameraAccess={this.state.hasCameraAccess}
												/>

												<Modal open={mpiPatient !== null}>
													<Modal.Header>{t("addPatient.patientFindInMpi")}</Modal.Header>
													<Modal.Content>
														<div>{t("addPatient.patientWithCnsFindInMpi")}</div>
														<div
															style={{
																color: "black",
																fontWeight: "bold",
																margin: "10px 0px",
															}}
														>
															{t("addPatient.checkInformationToUpdate")}
														</div>
														<FormatedMpiField
															title={t("addPatient.gender")}
															prop="gender"
															value={mpiPatient?.gender?.toLowerCase() === "m" ? "Homme" : "Femme"}
															selection={mpiSelection}
															onChange={this.onChangeMpiSelection}
														/>
														<FormatedMpiField
															title={t("addPatient.nameAtBirth")}
															prop="birth_name"
															value={mpiPatient?.birth_name}
															selection={mpiSelection}
															onChange={this.onChangeMpiSelection}
														/>
														<FormatedMpiField
															title={t("addPatient.marriedName")}
															prop="marital_name"
															value={mpiPatient?.marital_name}
															selection={mpiSelection}
															onChange={this.onChangeMpiSelection}
														/>
														<FormatedMpiField
															title={t("addPatient.firstName")}
															prop="first_name"
															value={mpiPatient?.first_name}
															selection={mpiSelection}
															onChange={this.onChangeMpiSelection}
														/>
														<FormatedMpiField
															title={t("addPatient.birthday")}
															prop="birth_date"
															value={mpiPatient?.birth_date ? moment(mpiPatient.birth_date).format(process.env.REACT_APP_DATE_DISPLAY_FORMAT) : null}
															selection={mpiSelection}
															onChange={this.onChangeMpiSelection}
														/>
														<FormatedMpiField
															title={t("addPatient.phone")}
															prop="phone"
															value={mpiPatient?.phone}
															selection={mpiSelection}
															onChange={this.onChangeMpiSelection}
														/>
														<FormatedMpiField
															title={t("addPatient.mobilePhone")}
															prop="mobile_phone"
															value={mpiPatient?.mobile_phone}
															selection={mpiSelection}
															onChange={this.onChangeMpiSelection}
														/>
														<FormatedMpiField
															title={t("addPatient.email")}
															prop="email"
															value={mpiPatient?.email}
															selection={mpiSelection}
															onChange={this.onChangeMpiSelection}
														/>
														{mpiPatient?.address && (
															<FormatedMpiField
																title={t("addPatient.address")}
																prop="address"
																value={`${mpiPatient.address.street} ${mpiPatient.address.complement} ${mpiPatient.address.postal_code} ${mpiPatient.address.city} ${mpiPatient.address.country}`}
																selection={mpiSelection}
																onChange={this.onChangeMpiSelection}
															/>
														)}
													</Modal.Content>
													<Modal.Actions>
														<Button content={t("addPatient.cancel")} onClick={this.cancelAutofillFromMpi} />
														<Button primary content={t("addPatient.update")} onClick={this.confirmAutofillFromMpi} />
													</Modal.Actions>
												</Modal>

												<Confirm
													open={this.state.openIgnoreCnsError}
													content={t("addPatient.ignoredCns")}
													cancelButton={t("addPatient.no")}
													confirmButton={t("addPatient.yes")}
													onCancel={this.cancelIgnoreCnsErrorConfirm}
													onConfirm={() => this.confirmIgnoreCnsErrorConfirm(setFieldValue)}
												/>
											</Form>
										);
									}}
								</Formik>
								<GranteePatientModal
									open={this.state.openGranteePatientModal}
									close={this.closeGranteePatientModal}
									grantee={this.state.granteeSelected}
									contactComments={contactComments}
									submit={this.validateGrantee}
									foundGrantee={this.state.foundGrantee}
									initFoundGrantee={this.initFoundGrantee}
									searchByMatricule={this.searchGranteeByMatricule}
									specialRegimes={specialRegimes}
								/>
							</Grid.Column>
						</Grid.Row>
					</Grid>
				</div>
			</div>
		);
	}
}

const mapStateToProps = (state) => {
	return {
		insurances: state.insurances,
		specialRegimes: state.specialRegimes,
		doctors: state.doctors,
		postalCodes: state.postalCodes,
		cities: state.cities,
		streets: state.streets,
		patients: state.patients,
		contactComments: state.contactComments,
		generalParameters: state.generalParameters,
	};
};

const mapDispatchToProps = {
	searchInsurances,
	getAllSpecialRegimes,
	getAllProviders,
	searchDoctors,
	searchPostalCodes,
	getCitiesByPostalCode,
	resetCities,
	resetWorkCities,
	getStreetsByPostalCode,
	getAllContactComments,
	getMpiPatientByMatricule,
	searchPatientsMyLab,
	getMatchingPatient,
	patchPatient,
	postPatient,
};

const FormatedMpiField = ({ title, value, prop, selection, onChange }) => {
	return (
		<div style={{ fontSize: "larger", justifyContent: "center", display: "flex", width: "fit-content" }}>
			<span style={{ fontWeight: "bold", color: "black", width: "200px" }}>{title}: </span>
			<span style={{ fontWeight: "bold", color: "black", width: "30px" }}>
				<Checkbox style={{ padding: "4px", marginRight: "10px" }} name={`check-` + title} checked={selection[prop]} onChange={() => onChange(prop)} />
			</span>
			{value && (prop === "phone" || prop === "mobile_phone") && `${formatPhoneNumberToInternational(value)}`}
			{prop !== "phone" && prop !== "mobile_phone" && value && value}
			{!value && <span style={{ fontStyle: "italic", color: "grey" }}>/</span>}
		</div>
	);
};

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(AddPatient));
